import dayjs from 'dayjs';
import _ from 'lodash';
import { TravelTypes, PaymentTypeCodes, BOOKING_FEES_LABEL, BookingStatus } from '../constants';
import computeTotalSeatCost from './computeTotalSeatCost';
import getCommissionLabel from './getCommissionLabel';
import getTourBookingStatus from './getTourBookingStatus';
import { getPurchasedInsurances } from './InsuranceHelper';

const currency = require('currency.js');

const checkIfItineraryOnHold = (trip, mode) =>
  trip &&
  trip.itinerariesOnHold &&
  !!_.find(trip.itinerariesOnHold, (it) => it.itineraryMode === mode);

const formatDepositItem = (cruiseTotals, deposit, roomIndex, hasTripConfirmation) => ({
  title: `${cruiseTotals.paymentSchedules.length > 1 ? `Stateroom ${roomIndex + 1}:` : ''} Cruise deposit ${!hasTripConfirmation ? `due ${dayjs(deposit.dueDate).format('MMM D, YYYY')}` : ''}`,
  price: +deposit.amount,
  roomIndex,
});

const formatFinalPayItem = (
  cruiseTotals,
  finalPayment,
  roomIndex,
  paymentAmount,
  doShowDueDate = true,
) => ({
  title: `${cruiseTotals.paymentSchedules.length > 1 ? `Stateroom ${roomIndex + 1}:` : ''} Final cruise payment ${doShowDueDate ? `due ${dayjs(finalPayment.dueDate).format('MMM D, YYYY')}` : ''}`,
  price: paymentAmount >= 0 ? +(paymentAmount || 0).toFixed(2) : +finalPayment.amount,
  roomIndex,
});

const getTotalPriceBreakdown = (
  trip,
  cruiseTotals,
  travelers,
  isTripSearch,
  tripConfirmation,
  agencyPlanningFees,
  carDirectBillingVendors = false,
  travelAgency,
  markupsByTripSegmentId,
) => {
  const lineItems = {};
  let total = currency(0);
  let dueNow = currency(0);
  let paid = currency(0);
  const paidItems = [];
  const dueNowItems = [];
  const payLaterItems = [];
  const commissionItems = [];
  const commissionLabels = [];
  const defaultCurrencyCode = 'USD';
  const isBookingResponseCancelled = (response) => {
    const status = response && response.bookingStatus;
    if (!status) return false;
    const parts = String(status)
      .split('/')
      .map((p) => p.trim())
      .filter(Boolean);
    return parts.length > 0 && parts.every((p) => p.toLowerCase().includes('cancel'));
  };

  const cruiseBookingResponses =
    tripConfirmation &&
    tripConfirmation.bookingResponses &&
    _.filter(tripConfirmation.bookingResponses, (b) => b.mode === TravelTypes.cruise);
  const cruiseBookingResponse = cruiseBookingResponses && cruiseBookingResponses[0];
  const isCruiseOnHold = !cruiseBookingResponse
    ? checkIfItineraryOnHold(trip, TravelTypes.cruise)
    : !cruiseBookingResponse.isPaymentMethodAdded;
  const isCruiseCancelled =
    cruiseBookingResponses &&
    cruiseBookingResponses.length > 0 &&
    _.every(cruiseBookingResponses, isBookingResponseCancelled);

  if (!isCruiseCancelled && cruiseTotals && cruiseTotals.totalAmount >= 0) {
    total = total.add(cruiseTotals.totalAmount);

    if (cruiseTotals.commission && cruiseTotals.commission.amount > 0) {
      commissionItems.push({
        title: 'Cruise',
        amount: cruiseTotals.commission.amount,
        currencyCode: cruiseTotals.commission.currencyCode,
      });
      commissionLabels.push(getCommissionLabel(travelAgency, TravelTypes.cruise));
    }
  }

  if (!isCruiseCancelled && cruiseBookingResponse && cruiseBookingResponse.paidAmount > 0) {
    // If already paid deposit, show "Cruise payment" label in paid items
    cruiseBookingResponses.forEach((r) => {
      paid = paid.add(r.paidAmount);
      paidItems.push({
        title: `Cruise payment ${cruiseBookingResponses.length > 1 ? `(Stateroom ${r.roomIndex + 1})` : ''}`,
        price: r.paidAmount,
        currencyCode: defaultCurrencyCode,
      });

      if (cruiseTotals && cruiseTotals.paymentSchedules) {
        _.filter(cruiseTotals.paymentSchedules, (s) => s.roomIndex === r.roomIndex).forEach((s) => {
          // Show final payment with due date if user made partial cruise payment
          if (s.finalPayment && +s.finalPayment.amount - r.paidAmount > 0) {
            if ((trip.addPaymentMethodToTravelTypes || []).indexOf(TravelTypes.cruise) === -1) {
              payLaterItems.push({
                ...formatFinalPayItem(
                  cruiseTotals,
                  s.finalPayment,
                  s.roomIndex,
                  +s.finalPayment.amount - r.paidAmount,
                ),
                currencyCode: defaultCurrencyCode,
              });
            } else {
              const paymentAmount = +(+s.finalPayment.amount - r.paidAmount).toFixed(2);

              const totalDueNowAmount =
                trip.cruisePayments && trip.cruisePayments[s.roomIndex]
                  ? trip.cruisePayments[s.roomIndex].paymentAmount
                  : paymentAmount;

              dueNow = dueNow.add(totalDueNowAmount);
              dueNowItems.push({
                ...formatFinalPayItem(
                  cruiseTotals,
                  s.finalPayment,
                  s.roomIndex,
                  paymentAmount,
                  false,
                ),
                isEditable: paymentAmount > 0,
                minValue: 0,
                maxValue: paymentAmount,
                currencyCode: defaultCurrencyCode,
              });

              payLaterItems.push({
                ...formatFinalPayItem(
                  cruiseTotals,
                  s.finalPayment,
                  s.roomIndex,
                  paymentAmount - totalDueNowAmount,
                ),
                currencyCode: defaultCurrencyCode,
              });
            }
          }
        });
      }
    });
  } else if (
    !isCruiseCancelled &&
    cruiseTotals &&
    cruiseTotals.totalDueNowAmount >= 0 &&
    ((!tripConfirmation && !isCruiseOnHold) ||
      (trip.addPaymentMethodToTravelTypes || []).indexOf(TravelTypes.cruise) > -1)
  ) {
    // FYI, trip.addPaymentMethodToTravelTypes is selected checkboxes in confirmation page,
    // when more than one modes on hold and user want to don't want to add payment to all on hold
    // So, if user don't want to add payment to cruise, it will be in pay later
    dueNow = dueNow.add(cruiseTotals.totalDueNowAmount);
    if (cruiseTotals.paymentSchedules && cruiseTotals.paymentSchedules.length) {
      cruiseTotals.paymentSchedules.forEach((s) => {
        if (s.deposit) {
          dueNowItems.push({
            ...formatDepositItem(cruiseTotals, s.deposit, s.roomIndex, !!tripConfirmation),
            isEditable: true,
            minValue: +s.deposit.amount,
            maxValue: s.finalPayment && +s.finalPayment.amount,
            currencyCode: defaultCurrencyCode,
          });
        }
        if (s.finalPayment) {
          const totalDueNowAmount =
            trip.cruisePayments && trip.cruisePayments[s.roomIndex]
              ? trip.cruisePayments[s.roomIndex].paymentAmount
              : +(s.deposit || {}).amount;
          payLaterItems.push({
            ...formatFinalPayItem(
              cruiseTotals,
              s.finalPayment,
              s.roomIndex,
              +s.finalPayment.amount - totalDueNowAmount,
            ),
            currencyCode: defaultCurrencyCode,
          });
        }
      });
    }
  } else if (!isCruiseCancelled && cruiseTotals && cruiseTotals.paymentSchedules) {
    cruiseTotals.paymentSchedules.forEach((s) => {
      if (s.deposit) {
        payLaterItems.push({
          ...formatDepositItem(cruiseTotals, s.deposit, s.roomIndex, !!tripConfirmation),
          currencyCode: defaultCurrencyCode,
        });
      }
      if (s.finalPayment) {
        payLaterItems.push({
          ...formatFinalPayItem(
            cruiseTotals,
            s.finalPayment,
            s.roomIndex,
            s.deposit ? +s.finalPayment.amount - +s.deposit.amount : null,
          ),
          currencyCode: defaultCurrencyCode,
        });
      }
    });
  }

  const flightBookingResp =
    trip.flightItinerary &&
    tripConfirmation &&
    tripConfirmation.bookingResponses &&
    _.find(tripConfirmation.bookingResponses, (b) => b.mode === TravelTypes.flight);
  const isFlightCancelled = isBookingResponseCancelled(flightBookingResp);
  if (trip.flightItinerary && !isFlightCancelled) {
    let flightTotalCost = trip.flightItinerary.cost ? trip.flightItinerary.cost.price : 0;
    const currencyCodeForFlight = (trip.flightItinerary.cost || {}).currencyCode || 'USD';

    let markupAmount = 0;
    if (flightBookingResp?.markupAmount > 0) {
      markupAmount = flightBookingResp.markupAmount;
    } else if (
      markupsByTripSegmentId &&
      markupsByTripSegmentId[trip.flightItinerary.tripSegmentId] > 0
    ) {
      markupAmount = +markupsByTripSegmentId[trip.flightItinerary.tripSegmentId];
    }

    let flightPriceBreakdown;
    if (markupAmount > 0) {
      flightPriceBreakdown = {
        markupAmount: markupAmount,
        netFare: flightTotalCost,
      };
    }
    flightTotalCost += markupAmount;
    total = total.add(flightTotalCost);

    const isTicketed =
      tripConfirmation && tripConfirmation.bookingResponses && (flightBookingResp || {}).isTicketed;
    if (isTicketed) {
      paid = paid.add(flightTotalCost);
      paidItems.push({
        title: 'Flight',
        price: flightTotalCost,
        currencyCode: currencyCodeForFlight,
        priceBreakdown: flightPriceBreakdown,
      });
    } else {
      payLaterItems.push({
        title: 'Flight (Once Ticketed)',
        price: flightTotalCost,
        currencyCode: currencyCodeForFlight,
        priceBreakdown: flightPriceBreakdown,
      });
    }

    if (travelers && !isTripSearch) {
      const totalFlightSeatPrice = computeTotalSeatCost(travelers, trip.flightItinerary);
      if (totalFlightSeatPrice > 0) {
        total = total.add(totalFlightSeatPrice);

        if (totalFlightSeatPrice > 0) {
          if (isTicketed) {
            paid = paid.add(totalFlightSeatPrice);
            paidItems.push({
              title: 'Flight seat',
              price: totalFlightSeatPrice,
              currencyCode: currencyCodeForFlight,
            });
          } else {
            payLaterItems.push({
              title: 'Flight seat (Once Ticketed)',
              price: totalFlightSeatPrice,
              currencyCode: currencyCodeForFlight,
            });
          }
        }
      }
    }

    const flightCommission = trip.flightItinerary.agentCommission || 0;
    if (flightCommission > 0) {
      commissionLabels.push(getCommissionLabel(travelAgency, TravelTypes.flight));
    }
    commissionItems.push({
      title: 'Flight',
      amount: flightCommission,
      currencyCode: currencyCodeForFlight,
    });
  }

  if (trip.hotelItineraries && Object.keys(trip.hotelItineraries).length > 0) {
    let hasAddedServiceFee = false;

    Object.values(trip.hotelItineraries).forEach((itinerary) => {
      const selectedHotelRoom = itinerary.hotel.products.find(
        (item) => item.code === itinerary.selectedRoomCode,
      );

      if (!selectedHotelRoom) {
        return;
      }

      const hotelBookingResponse =
        tripConfirmation &&
        tripConfirmation.bookingResponses &&
        _.find(
          tripConfirmation.bookingResponses,
          (b) => b.mode === TravelTypes.hotel && b.tripSegmentId === itinerary.tripSegmentId,
        );

      if (isBookingResponseCancelled(hotelBookingResponse)) {
        return;
      }

      const { originalPrice } = selectedHotelRoom.roomPrice;
      const { markupPrice } = selectedHotelRoom.roomPrice;

      const hasMarkup = itinerary.cost.doShowMarkupAmount && markupPrice > originalPrice;
      const markupAmount = +(markupPrice - originalPrice).toFixed(2);

      total = total.add(originalPrice);

      if (hotelBookingResponse && !hotelBookingResponse.bookingStatus) {
        payLaterItems.push({
          title: 'Hotel (once rebooked)',
          price: originalPrice,
          currencyCode: selectedHotelRoom.roomPrice.currencyCode,
        });
      } else if (
        (selectedHotelRoom.paymentTypeCode === PaymentTypeCodes.GUARANTEE ||
          selectedHotelRoom.paymentTypeCode === PaymentTypeCodes.DEPOSIT ||
          selectedHotelRoom.paymentTypeCode === PaymentTypeCodes.LATE) &&
        !selectedHotelRoom.isPrepaidRate
      ) {
        payLaterItems.push({
          title: 'Hotel (at check-in)',
          price: originalPrice,
          currencyCode: selectedHotelRoom.roomPrice.currencyCode,
        });
      } else if (
        hotelBookingResponse &&
        hotelBookingResponse.bookingReference &&
        selectedHotelRoom.isPrepaidRate
      ) {
        paid = paid.add(originalPrice);
        paidItems.push({
          title: 'Hotel',
          price: originalPrice,
          currencyCode: selectedHotelRoom.roomPrice.currencyCode,
        });
      } else {
        dueNow = dueNow.add(originalPrice);
        dueNowItems.push({
          title: 'Hotel',
          price: originalPrice,
          currencyCode: selectedHotelRoom.roomPrice.currencyCode,
        });
      }

      commissionItems.push({
        title: 'Hotel',
        amount: selectedHotelRoom.roomPrice.agentCommission,
        currencyCode: selectedHotelRoom.roomPrice.currencyCode,
      });
      if (selectedHotelRoom.roomPrice.agentCommission > 0) {
        commissionLabels.push(getCommissionLabel(travelAgency, TravelTypes.hotel));
      }

      if (hasMarkup && !hasAddedServiceFee) {
        if (hotelBookingResponse && hotelBookingResponse.bookingReference) {
          paid = paid.add(markupAmount);
          paidItems.push({
            title: 'Service Fee',
            price: markupAmount,
            currencyCode: selectedHotelRoom.roomPrice.currencyCode,
          });
        } else {
          dueNow = dueNow.add(markupAmount);
          dueNowItems.push({
            title: 'Service Fee',
            price: markupAmount,
            currencyCode: selectedHotelRoom.roomPrice.currencyCode,
          });
        }
        hasAddedServiceFee = true;
      }
    });
  }

  if (trip.rentalCarItineraries) {
    let carCommission = 0;
    let carCurrency = 'USD';
    let hasAddedServiceFee = false;

    Object.values(trip.rentalCarItineraries).forEach((item) => {
      const bookingResponse =
        tripConfirmation &&
        tripConfirmation.bookingResponses &&
        _.find(
          tripConfirmation.bookingResponses,
          (b) => b.mode === TravelTypes.carrental && b.tripSegmentId === item.tripSegmentId,
        );

      if (isBookingResponseCancelled(bookingResponse)) {
        return;
      }

      const originalPrice = item.cost.price;
      const { markupPrice } = item.cost;

      const hasMarkup = item.cost.doShowMarkupAmount && markupPrice > originalPrice;
      const markupAmount = +(markupPrice - originalPrice).toFixed(2);

      total = total.add(originalPrice);

      if (item && item.priceInfo.isPrepaidRate) {
        if (bookingResponse) {
          paid = paid.add(originalPrice);
          paidItems.push({
            title: 'Car',
            price: originalPrice,
            currencyCode: item.cost.currencyCode,
          });
        } else {
          dueNow = dueNow.add(originalPrice);
          dueNowItems.push({
            title: 'Car',
            price: originalPrice,
            currencyCode: item.cost.currencyCode,
          });
        }
      } else {
        const vendorCode = (item.bookingProviderInfo || {}).bookingProviderCode;
        payLaterItems.push({
          title: `Car (${vendorCode && carDirectBillingVendors && carDirectBillingVendors.length && _.find(carDirectBillingVendors, (r) => r.vendorCode === vendorCode) ? 'Direct Bill' : 'Pay at Pick-up'})`,
          price: originalPrice,
          currencyCode: item.cost.currencyCode,
        });
      }

      if (hasMarkup && !hasAddedServiceFee) {
        if (bookingResponse) {
          paid = paid.add(markupAmount);
          paidItems.push({
            title: 'Service Fee',
            price: markupAmount,
            currencyCode: item.cost.currencyCode,
          });
        } else {
          dueNow = dueNow.add(markupAmount);
          dueNowItems.push({
            title: 'Service Fee',
            price: markupAmount,
            currencyCode: item.cost.currencyCode,
          });
        }
        hasAddedServiceFee = true;
      }

      carCommission += item.priceInfo.agentCommission || 0;
      carCurrency = item.priceInfo.currencyCode;
    });

    if (carCommission > 0) {
      commissionLabels.push(getCommissionLabel(travelAgency, TravelTypes.carrental));
      commissionItems.push({ title: 'Car', amount: carCommission, currencyCode: carCurrency });
    }
  }
  (trip.tourItineraries || []).forEach((tourItinerary) => {
    const bookingResponse =
      tripConfirmation &&
      tripConfirmation.bookingResponses &&
      _.find(
        tripConfirmation.bookingResponses,
        (b) => b.mode === TravelTypes.tour && b.tripSegmentId === tourItinerary.tripSegmentId,
      );
    if (isBookingResponseCancelled(bookingResponse)) {
      return;
    }
    const tourPrice = tourItinerary.selectedSlot?.price || 0;
    const tourCurrencyCode =
      tourItinerary.selectedSlot?.currencyCode ||
      tourItinerary.cost?.currencyCode ||
      defaultCurrencyCode;
    const tourStatus = bookingResponse && getTourBookingStatus(bookingResponse);
    const isTourPaid = !!tourStatus && tourStatus !== BookingStatus.failed;
    const tourDueNow = tourItinerary.selectedSlot?.amountDueAtBooking ?? tourPrice;
    total = total.add(tourPrice);
    if (isTourPaid) {
      paid = paid.add(tourDueNow);
      paidItems.push({ title: 'Tour', price: tourDueNow, currencyCode: tourCurrencyCode });
    } else {
      dueNow = dueNow.add(tourDueNow);
      dueNowItems.push({ title: 'Tour', price: tourDueNow, currencyCode: tourCurrencyCode });
    }
    if (tourDueNow !== tourPrice) {
      payLaterItems.push({
        title: 'Tour, paid to the operator on the tour day',
        price: +(tourPrice - tourDueNow).toFixed(2),
        currencyCode: tourCurrencyCode,
      });
    }
    commissionLabels.push(getCommissionLabel(travelAgency, tourItinerary.mode));
  });

  if (trip.customInventoryItinerary) {
    const selectedProduct = trip.customInventoryItinerary.products.find(
      (item) => item.id === trip.customInventoryItinerary.selectedProductId,
    );
    const price = selectedProduct.pricing.markupPrice;
    total = total.add(price);
    dueNow = dueNow.add(price);
    dueNowItems.push({
      title: trip.customInventoryItinerary.type,
      price,
      currencyCode: defaultCurrencyCode,
    });
    commissionLabels.push(getCommissionLabel(travelAgency, trip.customInventoryItinerary.mode));
  }

  const purchasedInsurances = getPurchasedInsurances(tripConfirmation);
  if (tripConfirmation && purchasedInsurances) {
    purchasedInsurances.forEach((ii, index) => {
      const price = ii.total || 0;
      const currencyCode = ii.currencyCode || 'USD';

      total = total.add(price);
      paid = paid.add(price);

      paidItems.push({
        title: `Insurance Premium ${purchasedInsurances.length > 1 ? index + 1 : ''}`.trim(),
        price,
        currencyCode,
      });
    });
  }
  if (!tripConfirmation && trip.insuranceItinerary?.insuranceInfo?.quote) {
    const { quote, upgrades } = trip.insuranceItinerary.insuranceInfo;
    const price = quote.totalCost + (upgrades?.reduce((acc, u) => acc + u.price, 0) ?? 0);
    const currencyCode = quote.currency || 'USD';

    total = total.add(price);
    dueNow = dueNow.add(price);

    dueNowItems.push({
      title: 'Insurance Premium',
      price,
      currencyCode,
    });
  }

  let totalWithOldCruisePrice = total.value;
  if (
    !isCruiseCancelled &&
    trip.cruiseItinerary &&
    trip.cruiseItinerary.cost &&
    cruiseTotals &&
    cruiseTotals.totalAmount >= 0
  ) {
    totalWithOldCruisePrice =
      total.value - cruiseTotals.totalAmount + trip.cruiseItinerary.cost.price;
  }

  if (
    (isTripSearch && travelAgency && travelAgency.tripBookingFees > 0) ||
    (!isTripSearch && !tripConfirmation && trip.tripBookingFees > 0)
  ) {
    const tripBookingFees = isTripSearch ? travelAgency.tripBookingFees : trip.tripBookingFees;
    total = total.add(tripBookingFees);
    dueNow = dueNow.add(tripBookingFees);
    dueNowItems.push({
      title: BOOKING_FEES_LABEL,
      price: tripBookingFees,
      currencyCode: defaultCurrencyCode,
    });
  } else if (!isTripSearch && tripConfirmation && tripConfirmation.paidBookingFees > 0) {
    paid = paid.add(tripConfirmation.paidBookingFees);
    paidItems.push({
      title: BOOKING_FEES_LABEL,
      price: tripConfirmation.paidBookingFees,
      currencyCode: defaultCurrencyCode,
    });
  }

  lineItems.total = {
    title: 'Total', // cruise total + hotel + flight + car + tour
    price: total.value,
    oldPrice:
      totalWithOldCruisePrice > 0 && totalWithOldCruisePrice !== total.value
        ? totalWithOldCruisePrice
        : undefined,
  };

  lineItems.paid = {
    title: 'Total Paid', // cruise deposit + hotel + flight + car + tour
    price: paid.value,
    paidItems,
  };

  lineItems.dueNow = {
    title: 'Payment Now', // cruise deposit + hotel + flight + car + tour
    price: dueNow.value,
    dueNowItems,
  };

  lineItems.paymentLater = {
    title: 'Payment Later',
    price: +(total.value - dueNow.value - paid.value).toFixed(2),
    payLaterItems,
  };

  if (!isTripSearch && agencyPlanningFees > 0) {
    const hasServiceFeeNum =
      travelers &&
      travelers.length &&
      _.find(
        travelers,
        (t) =>
          t.travelerPreferences &&
          t.travelerPreferences.flightTicketNumbers &&
          t.travelerPreferences.flightTicketNumbers.length > 0,
      );
    if (hasServiceFeeNum) {
      lineItems.paid.price += agencyPlanningFees;
      paidItems.push({
        title: 'Agency planning fees',
        price: agencyPlanningFees,
        currencyCode: defaultCurrencyCode,
      });
    } else {
      lineItems.paymentLater.price += agencyPlanningFees;
      payLaterItems.push({
        title: 'Agency planning fees',
        price: agencyPlanningFees,
        currencyCode: defaultCurrencyCode,
      });
    }
    lineItems.total.price += agencyPlanningFees;
  }

  lineItems.total.totalItems = paidItems.concat(dueNowItems).concat(payLaterItems);

  lineItems.commission = {
    title:
      commissionLabels && _.uniqBy(commissionLabels).length === 1
        ? commissionLabels[0]
        : 'Est. commission', // Display default commission label if more than one mode have agent commission.
    price: commissionItems && commissionItems.length ? _.sumBy(commissionItems, 'amount') : 0, // TODO: refactor - this includes commission, so kinda misleading
    commissionItems,
  };

  lineItems.hasExcludedCancelledItem = (tripConfirmation?.bookingResponses || []).some(
    isBookingResponseCancelled,
  );
  return lineItems;
};

export default getTotalPriceBreakdown;
